import RenderManager from "../rendering/render-manager";
import { fire, listen } from "./events";
import PhysicalCameraControls from "../camera/physical-camera";
import FPSControls from "../camera/FPSControls";
import ObjectRotationControls from "../camera/object-rotation-controls";
import ObjectRotationControlsDom from "../camera/object-rotation-controls-dom";
import ObjectRotationControlsCoobo from "../camera/object-rotation-controls-coobo";
import initObjectRotationControls from './object-rotation-controls/init';
import StaticControls from "../camera/static-controls";
import { PivotControls } from "../pivots/pivot-controls";
import MultiCtrlFlag from "./multi-ctrl-flag";
import ObjRotCtrlsDisableWrapper from "./object-rotation-controls/obj-rot-ctrls-disable-wrapper";
import ObjRotCtrlsWrapper from "../pivots/obj-rot-ctrls-wrapper";

function addControls(LimitsCtl, passByRaycasterLoader, pivotSetterGetter) {

    const controlsPhysical = new PhysicalCameraControls(
        RenderManager.activeCamera,
        RenderManager.domElement,
        RenderManager.root,
        object => !!object.material
    );

    const controlsFps = new FPSControls(
        RenderManager.activeCamera,
        RenderManager.domElement,
        RenderManager.activeScene
    );

    const controlsObjRotMain = new ObjectRotationControls(
        RenderManager.activeCamera,
    );
    const controlsObjRotPivotWrap = new ObjRotCtrlsWrapper(controlsObjRotMain, pivotSetterGetter);
    const controlsObjRotDom = new ObjectRotationControlsDom(controlsObjRotPivotWrap, RenderManager.domElement);
    const controlsObjRot = new ObjectRotationControlsCoobo(controlsObjRotDom, controlsObjRotPivotWrap);
    const objRotDisableFlag = new MultiCtrlFlag((disable) => controlsObjRot.enabled = !disable);
    const controlsObjRotWrap = new ObjRotCtrlsDisableWrapper(controlsObjRot, objRotDisableFlag);

    const staticControls = new StaticControls(RenderManager.activeCamera);

    const pivotControls = new PivotControls(
        RenderManager.activeScene,
        RenderManager.activeCamera,
        RenderManager.domElement,
        pivotSetterGetter,
    );

    const pivotControlsKey = Symbol("PivotControls");
    pivotControls.events.addEventListener("select", ({ object }) => {
        objRotDisableFlag.set(pivotControlsKey, object);

        if (object && Q3.transformControls.object) {
            Q3.transformControls.detach();
        }
    });
    pivotControls.events.addEventListener("widgetChange", () => {
        RenderManager.activeCamera.update = true;
    });

    /*
     * Disable FPS controls while pivot transform widget is grabbed.
     * Not a clean solution. A better solution would disable the
     * controls without referring to particular control types, and
     * avoid enabling them if there's anything else blocking them.
     */
    let activateFpsOnRelease = false;
    pivotControls.events.addEventListener("widgetGrab", () => {
        if (controlsFps.enabled) {
            activateFpsOnRelease = true;
            controlsFps.deactivate();
        }
    });
    pivotControls.events.addEventListener("widgetRelease", () => {
        if (activateFpsOnRelease) {
            activateFpsOnRelease = false;
            controlsFps.activate();
        }
    });

    controlsPhysical.enableKeys = false;
    controlsPhysical.autoCentering = Q3.data.slides[0].controls
        ? Q3.data.slides[0].controls.autoCentering
        : Boolean(Q3.data.setups.controls.autoCentering);
    controlsPhysical.autoCenteringNeedsUpdate = false;
    controlsPhysical.autoCenterHelper = document.createElement("span");
    controlsPhysical.autoCenterHelper.id = "qbix-auto-center-helper";
    controlsPhysical.autoCenterHelper.className = "qbix-auto-center-helper-hide";
    controlsPhysical.autoCenterHelper.show = function () {
        controlsPhysical.autoCenterHelper.classList.add("qbix-auto-center-helper-show");
        controlsPhysical.autoCenterHelper.classList.remove("qbix-auto-center-helper-hide");
    };

    controlsPhysical.autoCenterHelper.hide = function () {
        controlsPhysical.autoCenterHelper.classList.remove("qbix-auto-center-helper-show");
        controlsPhysical.autoCenterHelper.classList.add("qbix-auto-center-helper-hide")
    };

    controlsPhysical.autoCenterHelper.timeoutID = null;

    controlsPhysical.addEventListener("start", function () {
        if (Q3.controls === controlsPhysical) {
            if (!(Q3.data.slides[0].controls
                ? Q3.data.slides[0].controls.autoCentering
                : Boolean(Q3.data.setups.controls.autoCentering))) return;
            controlsPhysical.autoCenteringNeedsUpdate = true;
            controlsPhysical.autoCenterHelper.show();
        }
    });

    controlsPhysical.addEventListener("end", function () {
        if (Q3.controls === controlsPhysical) {
            controlsPhysical.autoCenteringNeedsUpdate = true;
            controlsPhysical.autoCenterHelper.hide();
        }
    });

    Q3.isInteracting = false;

    controlsPhysical.addEventListener("start", function () { Q3.isInteracting = true });
    controlsPhysical.addEventListener("end", function () { Q3.isInteracting = false });
    controlsPhysical.addEventListener("change", function () { fire("camera_move") });
    controlsFps.events.addEventListener("change",
        function ({ azimuthSpeed, polarSpeed, forwardSpeed, leftSpeed }) {
            let a = Math.abs(azimuthSpeed);
            let p = Math.abs(polarSpeed);
            let f = Math.abs(forwardSpeed);
            let l = Math.abs(leftSpeed);
            if (a > 0.00001 || p > 0.00001 || f > 0.0005 || l > 0.0005) {
                fire("camera_move");
            }
        }
    );

    listen("camera_move", function () { RenderManager.activeCamera.update = true });

    if (!Q3.editMode) {
        setTimeout(function () {
            if (Q3.controls === controlsPhysical) {
                controlsPhysical.addEventListener("change", function () {

                    if (!Q3.isTransitioning) {
                        $("#qbix-viewer-button-replay").css("display", "inline");
                    }
                });
            }
        }, 2000);

    }

    Object.assign(controlsPhysical, Q3.data.setups.controls);

    if (Q3.browserCapabilities.isMobile) controlsPhysical.rotateSpeed *= 0.5;

    initObjectRotationControls(controlsObjRotWrap, controlsPhysical, LimitsCtl, RenderManager.activeCamera, passByRaycasterLoader, objRotDisableFlag);

    Q3.controls = controlsPhysical;
    Q3.physicalCameraControls = controlsPhysical;
    Q3.FPSControls = controlsFps;
    Q3.objectRotationControls = controlsObjRotWrap;
    Q3.staticControls = staticControls;
    Q3.objectPivots = {
        controls: pivotControls,
        setterGetter: pivotSetterGetter,
    };
}

export default addControls;
export { addControls };